import { FormControl, Input as MuiInput, Typography } from '@material-ui/core';
import { InputProps as MuiInputProps } from '@material-ui/core/Input';
import classNames from 'classnames';
import React, { FC, memo, ReactNode, useId } from 'react';

import useStyles from './styles';

export interface InputBaseProps {
  centered: boolean;
  error: string | boolean;
  hideErrorMessage: boolean;
  label: ReactNode;
  rootClass: string;
  size: 'small' | 'large' | number;
  variant: 'filled' | 'outlined' | 'standard';
  required?: boolean;
}
export type InputProps = Partial<InputBaseProps> & Omit<MuiInputProps, 'error'>;

export const Input: FC<InputProps> = ({
  'aria-label': ariaLabel,
  'aria-labelledby': ariaLabelledBy,
  'aria-describedby': ariaDescribedBy,
  label,
  error,
  centered,
  rootClass,
  hideErrorMessage,
  size = 'large',
  variant,
  required,
  inputProps,
  ...props
}) => {
  const styles = useStyles({ size, variant });
  const explicitLabel = inputProps?.['aria-label'] ?? ariaLabel;
  const explicitLabelledBy = inputProps?.['aria-labelledby'] ?? ariaLabelledBy;
  const instanceId = useId();
  const labelId = `input-label-${instanceId}`;
  const errorId = `input-error-${instanceId}`;
  const hasErrorText = typeof error === 'string' && error.length > 0 && !hideErrorMessage;
  const describedBy =
    [ariaDescribedBy, inputProps?.['aria-describedby'], hasErrorText ? errorId : undefined].filter(Boolean).join(' ') ||
    undefined;

  return (
    <FormControl
      classes={{
        root: styles.formControl,
      }}
      className={rootClass}
      hiddenLabel={!label}
    >
      {label && (
        <Typography
          id={labelId}
          classes={{ root: classNames(styles.label, { required }) }}
          color="textSecondary"
          variant="body2"
        >
          {label}
        </Typography>
      )}
      <MuiInput
        classes={{
          focused: styles.focused,
          input: classNames(styles.input, { centered }),
          root: classNames(styles.root, { error, [styles.multiline]: props.multiline }),
        }}
        disableUnderline
        {...props}
        inputProps={{
          ...inputProps,
          'aria-label': explicitLabel,
          'aria-labelledby': explicitLabelledBy ?? (!explicitLabel && label ? labelId : undefined),
          'aria-describedby': describedBy,
          'aria-invalid': error ? true : inputProps?.['aria-invalid'],
          'aria-required': required ? true : inputProps?.['aria-required'],
        }}
      />
      {error && !hideErrorMessage && (
        <Typography id={errorId} align="right" classes={{ root: styles.errorMessage }} color="error" variant="caption">
          {error}
        </Typography>
      )}
    </FormControl>
  );
};

export default memo(Input);
